import { TextButton, TextButtonSize } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { ChevronDownSmall } from 'wix-ui-icons-common';
import { FC, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import css from './index.module.scss';
import { Context } from '../../context';
import { SiteSelectorDropDown } from './SiteSelectorDropDown';
import { DATA_HOOKS } from '../../dataHooks';

export enum SiteSelectorParent {
  Hero = 'Hero',
  SearchBarModal = 'SearchBarModal',
}

type SiteSelectorProps = {
  itemId?: string;
  parent: SiteSelectorParent;
};

export const SiteSelector: FC<SiteSelectorProps> = ({ itemId, parent }) => {
  const { t } = useTranslation();
  const { siteSelectorData } = useContext(Context);
  const { selectedSite } = siteSelectorData;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const closePopover = () => setIsPopoverOpen(false);
  const onToggle = () => setIsPopoverOpen(!isPopoverOpen);

  const SiteDisplayName = useMemo(() => {
    if (selectedSite) {
      return selectedSite.siteProperties.siteDisplayName;
    } else {
      return t('site-selector.select-a-site');
    }
  }, [selectedSite, t]);

  if (!siteSelectorData.itemsCount) {
    return null;
  }

  const getSiteSelectorStyle = () => {
    switch (parent) {
      case SiteSelectorParent.Hero:
        return { textSize: 'medium', moveBy: { y: 10 }, width: '450px' };
      case SiteSelectorParent.SearchBarModal:
        // need to calc the move by according to the length of the site name.
        return { textSize: 'small', moveBy: { x: 12 }, width: '536px' };
      default:
        return {};
    }
  };

  const { textSize, width, moveBy } = getSiteSelectorStyle();

  return (
    <SiteSelectorDropDown
      shown={isPopoverOpen}
      onToggle={onToggle}
      onClickOption={closePopover}
      itemId={itemId}
      width={width}
      moveBy={moveBy}
    >
      <TextButton
        size={textSize as TextButtonSize}
        weight='bold'
        className={
          classNames(css.SiteSelectorPopoverOpenButton, { [css.SiteSelectorPopoverOpenButtonOpen]: isPopoverOpen })
        }
        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
        ellipsis
        suffixIcon={<ChevronDownSmall className={css.chevronDownIcon} />}
        dataHook={DATA_HOOKS.SITE_SELECTOR_DROPDOWN_BUTTON}
      >
        {SiteDisplayName}
      </TextButton>
    </SiteSelectorDropDown>
  );
};
